/**
 * CalendarEventPopover
 * Shown when clicking a calendar event — shows details and navigation links.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import type {
  CalendarEvent,
  AvailabilityType,
  SalesRepAvailability,
} from "../../types/salesCalendar.types";
import {
  AVAILABILITY_TYPE_LABELS,
  AVAILABILITY_TYPE_COLORS,
  EVENT_TYPE_LABELS,
} from "../../types/salesCalendar.types";
import Button from "../common/Button";
import Badge from "../ui/Badge";
import {
  XMarkIcon,
  MapPinIcon,
  CalendarIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";

interface CalendarEventPopoverProps {
  event: CalendarEvent;
  onClose: () => void;
  onEditAvailability?: (availability: SalesRepAvailability) => void;
  onAssignRep?: (eventId: string, eventTitle: string) => void;
  /** mig 1780: scheduling delegates who do not get the event page (an MSR) open Confirm Schedule instead */
  confirmScheduleOnly?: boolean;
}

/**
 * The last (inclusive) day of an all-day bar whose `end` is the exclusive midnight after it, or null for a one-day
 * bar. Calendar arithmetic, not `end - 86400000`: across a DST change that lands on the wrong date.
 */
export function allDayLastDay(
  start: Date,
  end: Date | null | undefined,
): Date | null {
  if (!end) {
    return null;
  }
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
  const first = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  return last > first ? last : null;
}

const CalendarEventPopover: React.FC<CalendarEventPopoverProps> = ({
  event,
  onClose,
  onEditAvailability,
  onAssignRep,
  confirmScheduleOnly = false,
}) => {
  const navigate = useNavigate();
  const {
    type,
    repName,
    repColor,
    originalData,
    locationName,
    locationAddress,
    availabilityType,
    eventType,
  } = event.resource;

  const eventTypeLabel = eventType
    ? EVENT_TYPE_LABELS[eventType] ||
      eventType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Event";

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-sm mx-4 shadow-xl">
        {/* Header with color bar */}
        <div
          className="h-1.5 rounded-t-lg"
          style={{ backgroundColor: repColor }}
        />
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {event.title}
              </h3>
              <div className="flex items-center gap-1.5 mt-1">
                <UserCircleIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{repName}</span>
                {type === "event" && (
                  <Badge
                    color="blue"
                    size="sm"
                    style={{
                      backgroundColor: repColor + "20",
                      color: repColor,
                    }}
                  >
                    {eventTypeLabel}
                  </Badge>
                )}
                {type === "availability" && (
                  <Badge
                    color="red"
                    size="sm"
                    style={{
                      backgroundColor:
                        (AVAILABILITY_TYPE_COLORS[
                          availabilityType as AvailabilityType
                        ] || "#ef4444") + "20",
                      color:
                        AVAILABILITY_TYPE_COLORS[
                          availabilityType as AvailabilityType
                        ] || "#ef4444",
                    }}
                  >
                    {AVAILABILITY_TYPE_LABELS[
                      availabilityType as AvailabilityType
                    ] || "Unavailable"}
                  </Badge>
                )}
                {type === "calendar_request" && (
                  <Badge color="yellow" size="sm">
                    Calendar Request
                  </Badge>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-2"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Date/time */}
          <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-2">
            <CalendarIcon className="h-4 w-4 text-gray-400" />
            {event.allDay ? (
              <span>
                {format(event.start, "MMM d, yyyy")}
                {(() => {
                  const last = allDayLastDay(event.start, event.end);
                  return last ? ` - ${format(last, "MMM d, yyyy")}` : null;
                })()}
              </span>
            ) : (
              <span>
                {format(event.start, "MMM d, yyyy h:mm a")}
                {event.end && ` - ${format(event.end, "h:mm a")}`}
              </span>
            )}
          </div>

          {/* Location */}
          {(locationName || locationAddress) && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-2">
              <MapPinIcon className="h-4 w-4 text-gray-400" />
              <span>
                {locationName}
                {locationAddress && locationName
                  ? ` · ${locationAddress}`
                  : locationAddress}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-4 pt-3 border-t">
            {type === "event" &&
              !(
                (originalData as any).event?.is_deleted ||
                (originalData as any).is_deleted
              ) && (
                <Button
                  size="sm"
                  onClick={() => {
                    // Join-table assignments have event_id; direct events have id
                    const eventId =
                      (originalData as any).event_id ||
                      (originalData as any).id;
                    navigate(
                      confirmScheduleOnly
                        ? `/schedule/events/${eventId}`
                        : `/events/${eventId}`,
                    );
                    onClose();
                  }}
                >
                  {confirmScheduleOnly ? "Confirm Schedule" : "View Event"}
                </Button>
              )}
            {type === "availability" && onEditAvailability && (
              <Button
                size="sm"
                onClick={() => {
                  onEditAvailability(originalData as SalesRepAvailability);
                  onClose();
                }}
              >
                Edit
              </Button>
            )}
            {type === "event" && onAssignRep && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const eventId =
                    (originalData as any).event_id || (originalData as any).id;
                  // GS-03: a per-day entry's title carries "(Day 1 of 2)"; Assign Rep adds the rep to the whole event
                  const od = originalData as {
                    title?: string;
                    event?: { title?: string };
                  };
                  onAssignRep(
                    eventId,
                    od.event?.title ?? od.title ?? event.title,
                  );
                  onClose();
                }}
              >
                Assign Rep
              </Button>
            )}
            {type === "calendar_request" && (
              <Button
                size="sm"
                onClick={() => {
                  navigate("/calendar-requests");
                  onClose();
                }}
              >
                View Requests
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarEventPopover;
